import { useCallback, useEffect, useId, useState } from 'react'
import type { FormEvent } from 'react'
import { MemoryStick, TriangleAlert } from 'lucide-react'
import type { AppSettingsDTO } from '@shared/types'
import { estimatedPoolRamMb } from '@shared/types'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Select,
  Skeleton,
  Switch,
  useToast
} from '@/components/ui'
import { api, errorMessage, isApiError } from '@/lib/api'
import { formatNumber, pluralize } from '@/lib/format'

const MIN_MAX_USERS = 1
const MAX_MAX_USERS = 16
const MIN_CONCURRENT_IMAGES = 1
const MAX_CONCURRENT_IMAGES = 8

/**
 * Four concurrent CLI processes at ~400 MB each. Past this a modest host is
 * paging rather than generating, so the number is worth an interruption.
 */
const MEMORY_WARN_MB = 1600

const DRIVER_OPTIONS = [
  { value: 'ssh2', label: 'ssh2 — keys stay in memory' },
  { value: 'system', label: 'system — shell out to the ssh binary' }
]

const DRIVER_DETAIL: Record<AppSettingsDTO['sshDriver'], string> = {
  ssh2: 'Argus opens the connection itself. A private key is decrypted into memory for one command and never written to disk — the safer default, and the one to keep unless a host refuses to negotiate.',
  system: 'Argus runs the ssh binary installed on the host. To do that it writes the private key to a 0600 file in its temp directory for the length of the command, then deletes it. Choose this only when ssh2 cannot connect, and only if you are comfortable with the key touching the filesystem.'
}

interface Draft {
  claudeCliPath: string
  maxConcurrentUsers: string
  maxConcurrentImages: string
  allowRegistration: boolean
  sshDriver: AppSettingsDTO['sshDriver']
}

function toDraft(settings: AppSettingsDTO): Draft {
  return {
    claudeCliPath: settings.claudeCliPath,
    maxConcurrentUsers: String(settings.maxConcurrentUsers),
    maxConcurrentImages: String(settings.maxConcurrentImages),
    allowRegistration: settings.allowRegistration,
    sshDriver: settings.sshDriver
  }
}

interface FieldErrors {
  claudeCliPath?: string
  maxConcurrentUsers?: string
  maxConcurrentImages?: string
  sshDriver?: string
  form?: string
}

function fieldErrorsFrom(details: Record<string, string[]> | undefined): FieldErrors {
  const next: FieldErrors = {}
  if (!details) return next

  for (const [key, messages] of Object.entries(details)) {
    const first = messages[0]
    if (!first) continue
    if (
      key === 'claudeCliPath' ||
      key === 'maxConcurrentUsers' ||
      key === 'maxConcurrentImages' ||
      key === 'sshDriver'
    ) {
      next[key] = first
    } else {
      next.form = first
    }
  }

  return next
}

/** GB past a gigabyte — "1.6 GB" lands where "1,600 MB" has to be converted first. */
function formatMemory(mb: number): string {
  if (mb < 1024) return `${formatNumber(mb)} MB`
  return `${(mb / 1024).toFixed(1)} GB`
}

function Derived({ label, value }: { label: string, value: string }): React.ReactElement {
  return (
    <div className="rounded-control border border-line bg-surface px-3 py-2">
      <p className="text-[10px] leading-tight font-semibold tracking-[0.08em] text-fg-subtle uppercase">
        {label}
      </p>
      <p className="mt-1 text-base leading-none font-semibold tabular-nums text-fg">{value}</p>
    </div>
  )
}

export default function InstanceSettingsCard(): React.ReactElement {
  const formId = useId()
  const { toast } = useToast()
  const [settings, setSettings] = useState<AppSettingsDTO | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [saving, setSaving] = useState(false)

  const load = useCallback(async (): Promise<void> => {
    setLoadError(null)
    try {
      const next = await api.get<AppSettingsDTO>('/admin/settings')
      setSettings(next)
      setDraft(toDraft(next))
    } catch (caught) {
      setLoadError(errorMessage(caught))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const update = (changes: Partial<Draft>): void => {
    setDraft((current) => (current ? { ...current, ...changes } : current))
  }

  const parsedUsers = draft ? Number(draft.maxConcurrentUsers) : Number.NaN
  const parsedPerSlot = draft ? Number(draft.maxConcurrentImages) : Number.NaN
  const usersValid =
    Number.isInteger(parsedUsers) && parsedUsers >= MIN_MAX_USERS && parsedUsers <= MAX_MAX_USERS
  const perSlotValid =
    Number.isInteger(parsedPerSlot) &&
    parsedPerSlot >= MIN_CONCURRENT_IMAGES &&
    parsedPerSlot <= MAX_CONCURRENT_IMAGES

  // Fall back to the saved value while a box is mid-edit, so the estimate never
  // blinks to zero as somebody clears a field to retype it.
  const effectiveUsers = usersValid ? parsedUsers : (settings?.maxConcurrentUsers ?? 0)
  const effectivePerSlot = perSlotValid ? parsedPerSlot : (settings?.maxConcurrentImages ?? 0)
  // Memory tracks the image pool alone. Raising the user cap changes who is allowed
  // to generate, not how many `claude` subprocesses exist at once.
  const slots = effectivePerSlot
  const estimatedMb = estimatedPoolRamMb({ maxConcurrentImages: effectivePerSlot })
  const memoryHeavy = estimatedMb > MEMORY_WARN_MB

  const dirty =
    settings !== null && draft !== null && JSON.stringify(toDraft(settings)) !== JSON.stringify(draft)

  const save = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (!settings || !draft) return

    if (!usersValid || !perSlotValid) {
      const invalid: FieldErrors = {}
      if (!usersValid) {
        invalid.maxConcurrentUsers = `Enter a whole number between ${MIN_MAX_USERS} and ${MAX_MAX_USERS}`
      }
      if (!perSlotValid) {
        invalid.maxConcurrentImages = `Enter a whole number between ${MIN_CONCURRENT_IMAGES} and ${MAX_CONCURRENT_IMAGES}`
      }
      setErrors(invalid)
      return
    }

    const patch: Partial<AppSettingsDTO> = {}
    const trimmedPath = draft.claudeCliPath.trim()
    if (trimmedPath !== settings.claudeCliPath) patch.claudeCliPath = trimmedPath
    if (parsedUsers !== settings.maxConcurrentUsers) patch.maxConcurrentUsers = parsedUsers
    if (parsedPerSlot !== settings.maxConcurrentImages) patch.maxConcurrentImages = parsedPerSlot
    if (draft.allowRegistration !== settings.allowRegistration) {
      patch.allowRegistration = draft.allowRegistration
    }
    if (draft.sshDriver !== settings.sshDriver) patch.sshDriver = draft.sshDriver

    if (Object.keys(patch).length === 0) {
      setErrors({})
      return
    }

    setErrors({})
    setSaving(true)
    try {
      const returned = await api.patch<AppSettingsDTO>('/admin/settings', patch)
      // A route that answers 204 leaves nothing to read back; fall through to the
      // values we just sent rather than blanking the form.
      const next: AppSettingsDTO =
        returned && typeof returned === 'object' ? returned : { ...settings, ...patch }
      setSettings(next)
      setDraft(toDraft(next))
      toast({ title: 'Instance settings saved', variant: 'success' })
    } catch (caught) {
      const mapped = isApiError(caught) ? fieldErrorsFrom(caught.details) : {}
      setErrors(Object.keys(mapped).length > 0 ? mapped : { form: errorMessage(caught) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Instance settings</CardTitle>
        <CardDescription>
          These apply to the whole server and to every account on it, including yours.
        </CardDescription>
      </CardHeader>

      {loadError ? (
        <CardBody className="space-y-3">
          <p className="text-sm text-bad-600 dark:text-bad-300">{loadError}</p>
          <Button
            variant="secondary"
            onClick={() => {
              void load()
            }}
          >
            Try again
          </Button>
        </CardBody>
      ) : !draft || !settings ? (
        <CardBody className="space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardBody>
      ) : (
        <>
          <CardBody>
            <form
              id={formId}
              noValidate
              className="space-y-6"
              onSubmit={(event) => {
                void save(event)
              }}
            >
              <Input
                label="Claude Code CLI path"
                value={draft.claudeCliPath}
                error={errors.claudeCliPath}
                placeholder="/usr/local/bin/claude"
                spellCheck={false}
                autoComplete="off"
                className="font-mono"
                hint="Absolute path to the claude binary on the server host — the program Argus spawns for every image. Leave it blank to let Argus resolve it from PATH. After changing it, open Settings → System status and choose Recheck."
                onChange={(event) => update({ claudeCliPath: event.target.value })}
              />

              <div className="space-y-4 border-t border-line pt-5">
                <div>
                  <h3 className="text-sm font-semibold text-fg">Generation slots</h3>
                  <p className="mt-1 text-xs leading-relaxed text-fg-muted">
                    Two numbers decide how much work this host takes on at once. A user who
                    arrives with every slot full is not refused — their job waits in the queue with
                    a visible position and starts on its own as soon as a slot frees.
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Users generating at once"
                    type="number"
                    inputMode="numeric"
                    min={MIN_MAX_USERS}
                    max={MAX_MAX_USERS}
                    step={1}
                    value={draft.maxConcurrentUsers}
                    error={errors.maxConcurrentUsers}
                    className="max-w-32 tabular-nums"
                    hint={`${MIN_MAX_USERS}–${MAX_MAX_USERS}. How many people may be generating simultaneously. Everyone after that queues.`}
                    onChange={(event) => update({ maxConcurrentUsers: event.target.value })}
                  />

                  <Input
                    label="Images per user, at once"
                    type="number"
                    inputMode="numeric"
                    min={MIN_CONCURRENT_IMAGES}
                    max={MAX_CONCURRENT_IMAGES}
                    step={1}
                    value={draft.maxConcurrentImages}
                    error={errors.maxConcurrentImages}
                    className="max-w-32 tabular-nums"
                    hint={`${MIN_CONCURRENT_IMAGES}–${MAX_CONCURRENT_IMAGES}. A shared pool across everyone generating, and the only setting that drives memory. One user working alone uses all of it.`}
                    onChange={(event) => update({ maxConcurrentImages: event.target.value })}
                  />
                </div>

                <div
                  aria-live="polite"
                  className="rounded-control border border-line bg-sunken p-3.5"
                >
                  <div className="grid gap-2.5 sm:grid-cols-3">
                    <Derived label="Users at once" value={formatNumber(effectiveUsers)} />
                    <Derived
                      label="Images in flight"
                      value={`${formatNumber(effectiveUsers)} × ${formatNumber(effectivePerSlot)} = ${formatNumber(slots)}`}
                    />
                    <Derived label="Estimated RAM" value={`~${formatMemory(estimatedMb)}`} />
                  </div>
                  <p className="mt-2.5 flex gap-2 text-xs leading-relaxed text-fg-muted">
                    <MemoryStick className="mt-0.5 size-3.5 shrink-0 text-fg-subtle" aria-hidden="true" />
                    <span>
                      Every in-flight image is a separate Claude Code CLI process, measured at about
                      392&nbsp;MB resident. {formatNumber(slots)}{' '}
                      {pluralize(slots, 'process')} is roughly {formatMemory(estimatedMb)} of memory
                      the host has to have spare on top of Postgres, Argus itself and the operating
                      system.
                    </span>
                  </p>
                </div>

                {memoryHeavy ? (
                  <div className="flex gap-2.5 rounded-control border border-warn-500/40 bg-warn-100/70 p-3.5 dark:bg-warn-950/50">
                    <TriangleAlert
                      className="mt-0.5 size-4 shrink-0 text-warn-700 dark:text-warn-300"
                      aria-hidden="true"
                    />
                    <div className="min-w-0 text-xs leading-relaxed text-warn-700 dark:text-warn-300">
                      <p className="font-semibold">
                        ~{formatMemory(estimatedMb)} of CLI processes is more than most hosts have
                        spare.
                      </p>
                      <p className="mt-1">
                        {formatNumber(effectiveUsers)} × {formatNumber(effectivePerSlot)} ={' '}
                        {formatNumber(slots)} claude processes can be resident at the same time, each
                        downloading an image and reading it. Once the machine starts swapping this
                        gets slower rather than faster, and every process shares the same CLI
                        authentication and rate limits. Keep the total near{' '}
                        {formatMemory(MEMORY_WARN_MB)} unless you know the host can take it — a
                        queued user waits a few minutes, an out-of-memory kill loses their work.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="border-t border-line pt-5">
                <Switch
                  checked={draft.allowRegistration}
                  onCheckedChange={(value) => update({ allowRegistration: value })}
                  label="Allow self-service registration"
                  description="When this is on, anyone who can reach this URL can create their own account and connect their own WP Engine sites — no invitation needed. Leave it off for a private instance and create accounts yourself on the Users tab."
                />
                {draft.allowRegistration ? (
                  <p className="mt-2 pl-14">
                    <Badge variant="warning" size="sm">
                      Open sign-up
                    </Badge>
                  </p>
                ) : null}
              </div>

              <div className="space-y-2 border-t border-line pt-5">
                <Select
                  label="SSH driver"
                  value={draft.sshDriver}
                  error={errors.sshDriver}
                  options={DRIVER_OPTIONS}
                  className="max-w-md"
                  onChange={(event) =>
                    update({ sshDriver: event.target.value === 'system' ? 'system' : 'ssh2' })
                  }
                />
                <p className="text-xs leading-relaxed text-fg-muted">
                  {DRIVER_DETAIL[draft.sshDriver]}
                </p>
              </div>

              {errors.form ? (
                <p className="text-sm font-medium text-bad-600 dark:text-bad-300">{errors.form}</p>
              ) : null}
            </form>
          </CardBody>

          <CardFooter className="justify-between">
            <p className="text-xs text-fg-subtle" aria-live="polite">
              {dirty
                ? 'Unsaved changes. New jobs pick these up as slots free; work already running is left alone.'
                : 'Everything here is saved.'}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                disabled={!dirty || saving}
                onClick={() => {
                  setDraft(toDraft(settings))
                  setErrors({})
                }}
              >
                Discard
              </Button>
              <Button type="submit" form={formId} loading={saving} disabled={!dirty}>
                Save changes
              </Button>
            </div>
          </CardFooter>
        </>
      )}
    </Card>
  )
}

export { InstanceSettingsCard }
